(function () {
  if (window.ORJNRewardsWidgetLoaded) return;
  window.ORJNRewardsWidgetLoaded = true;

  const rootId = 'orjn-rewards-widget';
  const config = window.ORJNRewardsConfig || {};
  const apiBase = config.apiBase || '/apps/orjn-rewards';
  const referralStorageKey = 'orjnReferralCode';
  const boundRoots = new WeakSet();
  const activityPageSize = 3;
  const activityState = new WeakMap();

  function mount() {
    storeReferralFromUrl();
    mountPageDashboard();
    mountFloatingPanel();
  }

  function mountPageDashboard() {
    const pageRoot = document.querySelector('[data-orjn-rewards-page]');
    if (!pageRoot) return;

    pageRoot.innerHTML = renderLoading('Loading your rewards');
    loadDashboard(pageRoot, { mode: 'page' });
  }

  function mountFloatingPanel() {
    if (document.getElementById(rootId)) return;

    const launcher = document.createElement('button');
    launcher.type = 'button';
    launcher.className = 'orjn-rewards-launcher';
    launcher.textContent = 'Rewards';
    launcher.setAttribute('aria-expanded', 'false');
    launcher.setAttribute('aria-controls', rootId);

    const panel = document.createElement('section');
    panel.id = rootId;
    panel.className = 'orjn-rewards-panel';
    panel.hidden = true;
    panel.innerHTML = renderLoading('Loading rewards');

    launcher.addEventListener('click', async () => {
      const isOpen = !panel.hidden;
      panel.hidden = isOpen;
      launcher.setAttribute('aria-expanded', String(!isOpen));

      if (!isOpen) {
        await loadDashboard(panel, { mode: 'panel' });
      }
    });

    bindDashboardActions(panel, { mode: 'panel' });
    document.body.append(launcher, panel);
  }

  function bindDashboardActions(root, options) {
    if (boundRoots.has(root)) return;
    boundRoots.add(root);

    root.addEventListener('click', async (event) => {
      const redeemButton = event.target.closest('[data-orjn-reward]');
      if (redeemButton) {
        await redeemReward(root, redeemButton, options);
        return;
      }

      const copyButton = event.target.closest('[data-orjn-copy]');
      if (copyButton) {
        await copyText(copyButton);
        return;
      }

      const applyCartButton = event.target.closest('[data-orjn-apply-cart]');
      if (applyCartButton) {
        await applyRewardToCart(applyCartButton);
        return;
      }

      const prevButton = event.target.closest('[data-orjn-activity-prev]');
      if (prevButton) {
        changeActivityPage(root, -1);
        return;
      }

      const nextButton = event.target.closest('[data-orjn-activity-next]');
      if (nextButton) {
        changeActivityPage(root, 1);
        return;
      }

      const refreshButton = event.target.closest('[data-orjn-rewards-refresh]');
      if (refreshButton) {
        await loadDashboard(root, options);
      }
    });

    root.addEventListener('submit', async (event) => {
      const birthdayForm = event.target.closest('[data-orjn-birthday-form]');
      if (birthdayForm) {
        event.preventDefault();
        await saveBirthday(root, birthdayForm, options);
      }

      const referralForm = event.target.closest('[data-orjn-referral-form]');
      if (referralForm) {
        event.preventDefault();
        await attachReferral(root, referralForm, options);
      }
    });
  }

  async function loadDashboard(root, options) {
    root.innerHTML = renderLoading(options.mode === 'page' ? 'Loading your rewards' : 'Loading rewards');
    bindDashboardActions(root, options);

    try {
      const response = await fetch(apiUrl('/me'), {
        headers: { Accept: 'application/json' },
        credentials: apiCredentials()
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data?.error || 'Unable to load your rewards right now.');

      if (data?.authenticated) {
        const attachedReferral = await attachStoredReferral(data);
        if (attachedReferral) {
          await loadDashboard(root, options);
          return;
        }
      }

      root.innerHTML = renderDashboard(data, options);
      activityState.set(root, { activity: data.activity || [], page: 1 });
      bindDashboardActions(root, options);
    } catch (error) {
      root.innerHTML = renderError(options, error?.message || 'Unable to load your rewards right now.');
    }
  }

  function renderDashboard(data, options) {
    if (!data?.authenticated) return renderGuest(options);

    const available = data.points?.available ?? 0;
    const pending = data.points?.pending ?? 0;
    const spent = data.points?.spent ?? 0;
    const firstName = data.customer?.firstName || data.customer?.email || 'Member';

    return `
      <div class="orjn-rewards-dashboard orjn-rewards-dashboard--${options.mode}">
        <section class="orjn-rewards-hero">
          <div>
            <span class="orjn-rewards-kicker">ORJN Rewards</span>
            <h2>${formatNumber(available)} points</h2>
            <p>${escapeHtml(firstName)}, your rewards are ready here. Earn on orders, redeem in cart, and keep your birthday saved.</p>
          </div>
          <div class="orjn-rewards-balance" aria-label="Rewards balance">
            ${renderBalanceItem('Available', available)}
            ${renderBalanceItem('Redeemed', pending, 'Points used on rewards you have not applied yet')}
            ${renderBalanceItem('Spent', spent)}
          </div>
        </section>

        <section class="orjn-rewards-section">
          <div class="orjn-rewards-section__head">
            <span>Redeem</span>
            <p>Use available points</p>
          </div>
          <div class="orjn-rewards-grid">
            ${Object.entries(data.rewards || {}).map(([key, reward]) => renderReward(key, reward)).join('')}
          </div>
        </section>

        <section class="orjn-rewards-section">
          <div class="orjn-rewards-section__head">
            <span>Earn</span>
            <p>Quick rules</p>
          </div>
          <div class="orjn-rewards-earn-list orjn-rewards-earn-list--compact">
            ${Object.values(data.earning || {}).map(renderEarnRule).join('')}
          </div>
        </section>

        <div class="orjn-rewards-two-col">
          ${renderBirthday(data.birthday)}
          ${renderReferral(data.referral)}
        </div>

        ${renderActivity(data.activity || [])}
        ${renderRedemptions(data.redemptions || [])}
      </div>
    `;
  }

  function renderGuest(options) {
    if (options.mode === 'page') {
      return `
        <div class="orjn-rewards-dashboard orjn-rewards-dashboard--${options.mode} orjn-rewards-dashboard--guest-page">
          ${renderStaticRewardsSection()}
          ${renderStaticEarnSection()}
        </div>
      `;
    }

    return `
      <div class="orjn-rewards-dashboard orjn-rewards-dashboard--${options.mode}">
        <section class="orjn-rewards-hero">
          <div>
            <span class="orjn-rewards-kicker">ORJN Rewards</span>
            <h2>Login to collect points</h2>
            <p>Use your ORJN account to collect points, redeem rewards, save your birthday, and share your referral link.</p>
          </div>
          <a class="orjn-rewards-primary" href="/account/login">Login / create account</a>
        </section>
        ${renderStaticEarnSection()}
      </div>
    `;
  }

  function renderStaticRewardsSection() {
    const rewards = [
      ['200 pts', 'Free delivery', 'Redeem for standard delivery.'],
      ['250 pts', '$10 off', 'Redeem for $10 off an order.'],
      ['450 pts', '$25 off', 'Redeem for $25 off an order.'],
      ['650 pts', '$40 off', 'Redeem for $40 off an order.']
    ];

    return `
      <section class="orjn-rewards-section">
        <div class="orjn-rewards-section__head">
          <span>Redeem</span>
          <p>Reward plan</p>
        </div>
        <div class="orjn-rewards-grid">
          ${rewards
            .map(
              ([points, label, detail]) => `
                <article class="orjn-rewards-card">
                  <div>
                    <span>${points}</span>
                    <h3>${label}</h3>
                    <p>${detail}</p>
                    <small>Login required</small>
                  </div>
                  <button type="button" disabled>Redeem</button>
                </article>
              `
            )
            .join('')}
        </div>
      </section>
    `;
  }

  function renderStaticEarnSection() {
    return `
      <section class="orjn-rewards-section">
        <div class="orjn-rewards-section__head">
          <span>Earn</span>
          <p>Launch rules</p>
        </div>
        <div class="orjn-rewards-earn-list orjn-rewards-earn-list--compact">
          ${renderEarnRule({ label: '$1 spent', points: 1, detail: 'Earn 1 point for every paid product dollar.' })}
          ${renderEarnRule({ label: 'Account signup', points: 50, detail: 'Get 50 points when your account is created.' })}
          ${renderEarnRule({ label: 'First order', points: 50, detail: 'Get 50 bonus points after your first order is paid.' })}
          ${renderEarnRule({ label: 'Birthday reward', points: 200, detail: 'Get 200 birthday points once per year.' })}
          ${renderEarnRule({ label: 'Refer a friend', points: 200, detail: 'Get 200 points after your friend completes their first order.' })}
        </div>
      </section>
    `;
  }

  function renderBalanceItem(label, value, hint) {
    const title = hint ? ` title="${escapeAttribute(hint)}"` : '';
    return `
      <div${title}>
        <span>${escapeHtml(label)}</span>
        <strong>${formatNumber(value)}</strong>
      </div>
    `;
  }

  function renderReward(key, reward) {
    const canRedeem = Boolean(reward.canRedeem);
    const shortText = canRedeem ? 'Ready now' : `Need ${formatNumber(reward.pointsShort || 0)} more`;
    return `
      <article class="orjn-rewards-card">
        <div>
          <span>${formatNumber(reward.points)} pts</span>
          <h3>${escapeHtml(reward.label)}</h3>
          <p>${escapeHtml(reward.description)}</p>
          <small>${escapeHtml(shortText)}</small>
        </div>
        <button type="button" data-orjn-reward="${escapeAttribute(key)}" ${canRedeem ? '' : 'disabled'}>
          Redeem
        </button>
      </article>
    `;
  }

  function renderEarnRule(rule) {
    return `
      <article class="orjn-rewards-earn">
        <div>
          <h3>${escapeHtml(rule.label)}</h3>
          <p>${escapeHtml(rule.detail)}</p>
        </div>
        <strong>${formatNumber(rule.points)} pts</strong>
      </article>
    `;
  }

  function renderBirthday(birthday) {
    const isSet = Boolean(birthday?.isSet);
    const points = formatNumber(birthday?.points ?? 200);

    if (isSet) {
      const birthdayLabel = formatMonthDay(birthday.month, birthday.day);
      const nextDate = birthday?.nextRewardDate ? formatDate(birthday.nextRewardDate) : '';
      return `
        <section class="orjn-rewards-section">
          <div class="orjn-rewards-section__head">
            <span>Birthday</span>
            <p>Saved</p>
          </div>
          <p class="orjn-rewards-copy">Your birthday is <strong>${escapeHtml(birthdayLabel)}</strong>.</p>
          <p class="orjn-rewards-copy">Your ${points} point birthday reward arrives on <strong>${escapeHtml(nextDate)}</strong>.</p>
          <small class="orjn-rewards-muted">Your birthday is locked in and cannot be changed.</small>
        </section>
      `;
    }

    return `
      <section class="orjn-rewards-section">
        <div class="orjn-rewards-section__head">
          <span>Birthday</span>
          <p>Add date</p>
        </div>
        <p class="orjn-rewards-copy">Add your birthday to get ${points} points once per year. This can only be set once, so double-check the date.</p>
        <form class="orjn-rewards-form" data-orjn-birthday-form>
          <label>
            <span>Month</span>
            <select name="month" required>
              <option value="" disabled selected>Month</option>
              ${monthOptions()}
            </select>
          </label>
          <label>
            <span>Day</span>
            <select name="day" required>
              <option value="" disabled selected>Day</option>
              ${dayOptions()}
            </select>
          </label>
          <button type="submit">Save birthday</button>
        </form>
      </section>
    `;
  }

  function monthOptions() {
    const names = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    return names
      .map((name, index) => `<option value="${index + 1}">${name}</option>`)
      .join('');
  }

  function dayOptions() {
    let options = '';
    for (let day = 1; day <= 31; day += 1) {
      options += `<option value="${day}">${day}</option>`;
    }
    return options;
  }

  function formatMonthDay(month, day) {
    const m = Number(month || 0);
    const d = Number(day || 0);
    if (!m || !d) return '';
    const date = new Date(Date.UTC(2024, m - 1, d));
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleDateString(undefined, { month: 'long', day: 'numeric', timeZone: 'UTC' });
  }

  function renderReferral(referral) {
    const path = referral?.url || '';
    const fullUrl = path ? `${window.location.origin}${path}` : '';
    const storedCode = getStoredReferralCode();
    const referralPoints = referral?.points ?? 200;
    const successfulReferrals = Number(referral?.successfulReferrals ?? 0);
    return `
      <section class="orjn-rewards-section">
        <div class="orjn-rewards-section__head">
          <span>Refer</span>
          <p>${formatNumber(referralPoints)} pts per friend</p>
        </div>
        <p class="orjn-rewards-copy">Share your link. You get ${formatNumber(referralPoints)} points after your friend completes their first order.</p>
        <div class="orjn-rewards-code">
          <span>${escapeHtml(referral?.code || '')}</span>
          <button type="button" data-orjn-copy="${escapeAttribute(fullUrl)}">Copy link</button>
        </div>
        <p class="orjn-rewards-muted">${formatNumber(successfulReferrals)} ${successfulReferrals === 1 ? 'friend has' : 'friends have'} completed a first order with your code.</p>
        ${
          storedCode && !referral?.referredByCode
            ? `<form class="orjn-rewards-form orjn-rewards-form--single" data-orjn-referral-form>
                <input type="hidden" name="referralCode" value="${escapeAttribute(storedCode)}">
                <button type="submit">Attach referral code ${escapeHtml(storedCode)}</button>
              </form>`
            : ''
        }
      </section>
    `;
  }

  function renderActivity(activity) {
    if (!activity.length) {
      return `
        <section class="orjn-rewards-section">
          <div class="orjn-rewards-section__head">
            <span>Activity</span>
            <p>Ledger</p>
          </div>
          <p class="orjn-rewards-copy">Your points activity will appear here after signup, orders, birthdays, referrals, or redemptions.</p>
        </section>
      `;
    }

    return `
      <section class="orjn-rewards-section" data-orjn-activity>
        <div class="orjn-rewards-section__head">
          <span>Activity</span>
          <p>Latest points</p>
        </div>
        ${renderActivityBody(activity, 1)}
      </section>
    `;
  }

  function renderActivityBody(activity, page) {
    const totalPages = Math.max(1, Math.ceil(activity.length / activityPageSize));
    const currentPage = Math.min(Math.max(1, page), totalPages);
    const start = (currentPage - 1) * activityPageSize;
    const rows = activity.slice(start, start + activityPageSize);

    return `
      <div class="orjn-rewards-activity">
        ${rows.map(renderActivityRow).join('')}
      </div>
      ${
        totalPages > 1
          ? `
        <div class="orjn-rewards-activity-pager">
          <button type="button" data-orjn-activity-prev ${currentPage <= 1 ? 'disabled' : ''}>Previous</button>
          <span>Page ${currentPage} of ${totalPages}</span>
          <button type="button" data-orjn-activity-next ${currentPage >= totalPages ? 'disabled' : ''}>Next</button>
        </div>
      `
          : ''
      }
    `;
  }

  function changeActivityPage(root, delta) {
    const state = activityState.get(root);
    if (!state) return;

    const totalPages = Math.max(1, Math.ceil(state.activity.length / activityPageSize));
    const nextPage = Math.min(Math.max(1, state.page + delta), totalPages);
    if (nextPage === state.page) return;
    state.page = nextPage;

    const section = root.querySelector('[data-orjn-activity]');
    if (!section) return;
    const head = section.querySelector('.orjn-rewards-section__head');
    section.innerHTML = (head ? head.outerHTML : '') + renderActivityBody(state.activity, state.page);
  }

  function renderActivityRow(entry) {
    const sign = Number(entry.points) > 0 ? '+' : '';
    const note = entry.note || entry.shopifyOrderName || '';
    const date = formatDate(entry.createdAt);
    const balance = Number.isFinite(Number(entry.balanceAfter)) ? `Balance after: ${formatNumber(entry.balanceAfter)}` : '';
    const detail = [note, date, balance].filter(Boolean).join(' - ');
    return `
      <div>
        <span>${escapeHtml(formatLedgerType(entry.type))}</span>
        <small>${escapeHtml(detail)}</small>
        <strong>${sign}${formatNumber(entry.points)} pts</strong>
      </div>
    `;
  }

  function renderRedemptions(redemptions) {
    const active = redemptions.filter((redemption) => redemption.status === 'ISSUED');
    if (!active.length) return '';

    return `
      <section class="orjn-rewards-section">
        <div class="orjn-rewards-section__head">
          <span>Active rewards</span>
          <p>Issued codes</p>
        </div>
        <div class="orjn-rewards-activity">
          ${active
            .map(
              (redemption) => `
                <div>
                  <span>${escapeHtml(redemption.discountCode)}</span>
                  <small>${redemption.expiresAt ? `Expires ${escapeHtml(formatDate(redemption.expiresAt))}` : 'No expiry date stored'}</small>
                  <button type="button" data-orjn-copy="${escapeAttribute(redemption.discountCode)}">Copy</button>
                </div>
              `
            )
            .join('')}
        </div>
      </section>
    `;
  }

  async function redeemReward(root, button, options) {
    if (button.disabled) return;

    const rewardKey = button.getAttribute('data-orjn-reward');
    button.disabled = true;
    button.textContent = 'Issuing';

    try {
      const response = await fetch(apiUrl('/redeem'), {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        credentials: apiCredentials(),
        body: JSON.stringify({ rewardKey })
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data?.error || 'Unable to redeem reward.');

      storeIssuedReward(data);
      root.innerHTML = renderIssuedReward(data, options);
      bindDashboardActions(root, options);
    } catch (error) {
      const message = error.message || 'Unable to redeem reward.';
      await loadDashboard(root, options);
      root.insertAdjacentHTML('afterbegin', renderNotice(message, 'error'));
    }
  }

  async function saveBirthday(root, form, options) {
    const button = form.querySelector('button[type="submit"]');
    const month = Number(form.elements.month ? form.elements.month.value : 0);
    const day = Number(form.elements.day ? form.elements.day.value : 0);
    if (button) {
      button.disabled = true;
      button.textContent = 'Saving';
    }

    try {
      if (!month || !day) throw new Error('Choose your birthday month and day first.');

      const response = await fetch(apiUrl('/birthday'), {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        credentials: apiCredentials(),
        body: JSON.stringify({ month, day })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data?.error || 'Unable to save birthday.');

      const birthday = data?.birthday;
      const birthdayLabel = birthday ? formatMonthDay(birthday.month, birthday.day) : '';
      const nextDate = birthday?.nextRewardDate ? formatDate(birthday.nextRewardDate) : '';
      const points = formatNumber(birthday?.points ?? 200);
      const confirmation = birthdayLabel && nextDate
        ? `Birthday saved as ${birthdayLabel}. Your ${points} point reward arrives on ${nextDate}.`
        : 'Birthday saved.';

      root.innerHTML = renderDashboard({ authenticated: true, ...data }, options);
      activityState.set(root, { activity: data.activity || [], page: 1 });
      root.insertAdjacentHTML('afterbegin', renderNotice(confirmation, 'success'));
      bindDashboardActions(root, options);
    } catch (error) {
      root.insertAdjacentHTML('afterbegin', renderNotice(error.message || 'Unable to save birthday.', 'error'));
      if (button) {
        button.disabled = false;
        button.textContent = 'Save birthday';
      }
    }
  }

  async function attachReferral(root, form, options) {
    const referralCode = form.elements.referralCode.value;
    try {
      const response = await fetch(apiUrl('/referral'), {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        credentials: apiCredentials(),
        body: JSON.stringify({ referralCode })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data?.error || 'Unable to attach referral.');
      clearStoredReferralCode();
      root.innerHTML = renderDashboard({ authenticated: true, ...data }, options);
      activityState.set(root, { activity: data.activity || [], page: 1 });
      bindDashboardActions(root, options);
    } catch (error) {
      root.insertAdjacentHTML('afterbegin', renderNotice(error.message || 'Unable to attach referral.', 'error'));
    }
  }

  function renderIssuedReward(data, options) {
    const code = data?.redemption?.discountCode || '';
    const expiresAt = data?.redemption?.expiresAt ? formatDate(data.redemption.expiresAt) : '30 days';

    return `
      <div class="orjn-rewards-issued-view orjn-rewards-dashboard--${options.mode}">
        <span class="orjn-rewards-kicker">ORJN Rewards</span>
        <h2>Reward issued</h2>
        <p>Use this code before ${escapeHtml(expiresAt)}.</p>
        <div class="orjn-rewards-code">
          <span>${escapeHtml(code)}</span>
          <button type="button" data-orjn-copy="${escapeAttribute(code)}">Copy</button>
        </div>
        <button type="button" class="orjn-rewards-primary" data-orjn-apply-cart="${escapeAttribute(code)}">Apply to cart</button>
        <button type="button" class="orjn-rewards-secondary" data-orjn-rewards-refresh>Back to rewards</button>
      </div>
    `;
  }

  function renderLoading(title) {
    return `
      <div class="orjn-rewards-loading">
        <span class="orjn-rewards-kicker">ORJN Rewards</span>
        <h2>${escapeHtml(title)}</h2>
      </div>
    `;
  }

  function renderError(options, message) {
    const safeMessage = escapeHtml(message || 'Unable to load your rewards right now.');
    if (options.mode === 'page') {
      return `
        <div class="orjn-rewards-dashboard orjn-rewards-dashboard--${options.mode} orjn-rewards-dashboard--guest-page">
          <p class="orjn-rewards-notice orjn-rewards-notice--error">${safeMessage}</p>
          <button type="button" class="orjn-rewards-primary" data-orjn-rewards-refresh>Try again</button>
          ${renderStaticRewardsSection()}
          ${renderStaticEarnSection()}
        </div>
      `;
    }

    return `
      <div class="orjn-rewards-dashboard orjn-rewards-dashboard--${options.mode}">
        <section class="orjn-rewards-hero">
          <div>
            <span class="orjn-rewards-kicker">ORJN Rewards</span>
            <h2>Could not load your balance</h2>
            <p>${safeMessage}</p>
          </div>
          <button type="button" class="orjn-rewards-primary" data-orjn-rewards-refresh>Try again</button>
        </section>
        ${renderStaticRewardsSection()}
        ${renderStaticEarnSection()}
      </div>
    `;
  }

  function renderNotice(message, type) {
    return `<p class="orjn-rewards-notice orjn-rewards-notice--${type}">${escapeHtml(message)}</p>`;
  }

  async function attachStoredReferral(data) {
    const storedCode = getStoredReferralCode();
    if (!storedCode || data?.referral?.referredByCode) return false;

    return fetch(apiUrl('/referral'), {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      credentials: apiCredentials(),
      body: JSON.stringify({ referralCode: storedCode })
    }).then((response) => {
      if (response.ok) {
        clearStoredReferralCode();
        return true;
      }

      return false;
    }).catch(() => {});
  }

  function storeIssuedReward(data) {
    const code = data?.redemption?.discountCode || '';
    if (!code) return;

    try {
      window.localStorage.setItem('orjnLoyaltyRewardCode', code);
      window.localStorage.setItem(
        'orjnLoyaltyReward',
        JSON.stringify({
          code,
          rewardType: data?.redemption?.type || '',
          applyUrl: data?.applyUrl || '',
          issuedAt: new Date().toISOString()
        })
      );
    } catch {
      // Customers can still use the visible code/link when storage is blocked.
    }
  }

  function apiUrl(path) {
    const url = new URL(`${apiBase}${path}`, window.location.origin);
    if (config.customerId && config.customerToken) {
      url.searchParams.set('customer_id', config.customerId);
      url.searchParams.set('customer_token', config.customerToken);
    }
    return url.toString();
  }

  function apiCredentials() {
    return apiBase.startsWith('http') ? 'omit' : 'same-origin';
  }

  function storeReferralFromUrl() {
    const ref = new URLSearchParams(window.location.search).get('ref');
    if (!ref) return;
    try {
      window.localStorage.setItem(referralStorageKey, ref.trim().toUpperCase());
    } catch {}
  }

  function getStoredReferralCode() {
    try {
      return (window.localStorage.getItem(referralStorageKey) || '').trim().toUpperCase();
    } catch {
      return '';
    }
  }

  function clearStoredReferralCode() {
    try {
      window.localStorage.removeItem(referralStorageKey);
    } catch {}
  }

  async function copyText(button) {
    const text = button.getAttribute('data-orjn-copy') || '';
    try {
      await navigator.clipboard.writeText(text);
      button.textContent = 'Copied';
    } catch {
      button.textContent = text;
    }
  }

  // Apply a reward to the cart via the AJAX Cart API (the discount lives in the cart,
  // not a sticky cookie), then send the customer to the cart. This avoids the legacy
  // /discount/CODE link, whose cookie could override the cart at checkout.
  async function applyRewardToCart(button) {
    const code = button.getAttribute('data-orjn-apply-cart') || '';
    if (!code) return;

    const label = button.textContent;
    button.disabled = true;
    button.textContent = 'Applying...';

    try {
      const base = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/';
      await fetch(`${base}cart/update.js`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({ discount: code })
      });
      // Keep Shopify's discount cookie in step with the cart so checkout matches.
      try {
        document.cookie = `discount_code=${encodeURIComponent(code)}; path=/; SameSite=Lax`;
      } catch {}
      window.location.href = `${base}cart`;
    } catch (error) {
      // Fall back to the discount link if the AJAX call fails for any reason.
      button.disabled = false;
      button.textContent = label;
      window.location.href = `/discount/${encodeURIComponent(code)}?redirect=/cart`;
    }
  }

  function formatLedgerType(type) {
    return String(type || '').toLowerCase().replaceAll('_', ' ');
  }

  function formatNumber(value) {
    return Number(value || 0).toLocaleString();
  }

  function formatDate(value) {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value || '');
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  }

  function escapeHtml(value) {
    return String(value)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');
  }

  function escapeAttribute(value) {
    return escapeHtml(value).replaceAll('`', '&#096;');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', mount, { once: true });
  } else {
    mount();
  }
})();
